/**
 * Magniberg shipping-country picker (snippets/mb-country-picker.liquid).
 *
 * - Typing in the search field narrows the list to the countries whose name contains
 *   the text; the letter headings are hidden while searching.
 * - Choosing a country submits Shopify's localization form, which reloads the page in
 *   that country's market.
 */
class MbCountryPicker extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    // A search field would otherwise submit nothing useful on Enter.
    this.addEventListener('keydown', this.#onKeyDown, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** @param {Event} event */
  #onInput = (event) => {
    const field = event.target;
    if (!(field instanceof HTMLInputElement) || field.type !== 'search') return;

    const term = field.value.trim().toLowerCase();
    this.toggleAttribute('searching', term !== '');

    for (const country of this.querySelectorAll('.mb-country-picker__country')) {
      if (!(country instanceof HTMLElement)) continue;
      country.hidden = term !== '' && !(country.dataset.name ?? '').includes(term);
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    const form = input.form;
    if (!form) return;

    this.setAttribute('aria-busy', 'true');
    form.submit();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'search') return;

    event.preventDefault();
    // Enter picks the only match, if there is exactly one.
    const matches = this.querySelectorAll('.mb-country-picker__country:not([hidden]) input[type="radio"]');
    if (matches.length === 1 && matches[0] instanceof HTMLInputElement) matches[0].click();
  };
}

if (!customElements.get('mb-country-picker')) {
  customElements.define('mb-country-picker', MbCountryPicker);
}
